import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import type { OnlineStatusFilter } from '@/entities/clients-filter.entity';
import { cn } from '@/lib/utils';
import { ListIcon, WifiIcon, WifiOffIcon } from 'lucide-react';

const ONLINE_OPTIONS: { value: OnlineStatusFilter; label: string; icon: typeof ListIcon }[] = [
	{ value: 'all', label: 'Все', icon: ListIcon },
	{ value: 'online', label: 'Онлайн', icon: WifiIcon },
	{ value: 'offline', label: 'Офлайн', icon: WifiOffIcon },
];

interface OnlineFilterGroupProps {
	value: OnlineStatusFilter;
	onChange: (value: OnlineStatusFilter) => void;
	onlineCount?: number | null;
	className?: string;
	/** Icon-only buttons for narrow chrome */
	compact?: boolean;
}

export default function OnlineFilterGroup({ value, onChange, onlineCount, className, compact }: OnlineFilterGroupProps) {
	return (
		<div className={cn(compact ? 'flex' : 'flex justify-center', className)}>
			<ButtonGroup>
				{ONLINE_OPTIONS.map(({ value: option, label, icon: Icon }) => {
					const isActive = value === option;
					const isOnline = option === 'online';
					const isOffline = option === 'offline';

					return (
						<Button
							key={option}
							variant={isActive ? 'secondary' : 'outline'}
							onClick={() => onChange(option)}
							className={cn('gap-1.5', compact && 'px-2.5')}
							size={compact ? 'sm' : 'default'}
							aria-label={label}
							title={label}
						>
							{isOnline && isActive ? (
								<span className='flex items-center gap-1'>
									<span className='bg-chart-2 w-2 h-2 animate-pulse rounded-full' />
									<Icon size={16} className='text-chart-2' />
								</span>
							) : (
								<Icon size={16} className={isOnline ? 'text-chart-2' : isOffline ? 'text-chart-5' : undefined} />
							)}
							{!compact && (
								<span className={isOnline && isActive ? 'text-chart-2' : isOffline && isActive ? 'text-chart-5' : undefined}>
									{label}
								</span>
							)}
							{isOnline && onlineCount != null && (
								<span className={cn('tabular-nums font-semibold text-xs', isActive && 'text-chart-2')}>{onlineCount}</span>
							)}
						</Button>
					);
				})}
			</ButtonGroup>
		</div>
	);
}
